import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { ToastrService } from 'ngx-toastr';
import { flatMap } from 'rxjs/operators';
import { UploadDetails, UploadMessageTranslation } from './file-upload.model';

@Component({
    selector: 'odq-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    standalone: false
})
export class FileUploadComponent implements OnInit, OnChanges {
  @Input() title: string;
  @Input() url: string;
  @Input() documentType: string;
  @Input() authToken: string;

  @Output() onSuccessUpload = new EventEmitter<UploadDetails>();
  @ViewChild('file', { static: true }) file: ElementRef;

  uploader: FileUploader;
  hasBaseDropZoneOver = false;

  constructor(private toastrService: ToastrService, private translate: TranslateService) {
    this.uploader = new FileUploader({
      url: this.url,
      isHTML5: true,
      removeAfterUpload: false,
      disableMultipart: false,
      headers: [{
        name: 'Access-Control-Request-Headers', value: 'true',
      }],
      filters: [],
      maxFileSize: 10 * 1024 * 1024,
      formatDataFunction: (item: FileItem) => ({ file: item._file, type: this.documentType }),
      formatDataFunctionIsAsync: false,
    });
  }

  ngOnInit(): void {
    this.uploader.onAfterAddingFile = (fileItem) => this.manageSingleQueue();
    this.uploader.onErrorItem = (item, response, status, headers) => {
      const notify = this.getUploadFailedTranslation();
      this.showError(notify.header, notify.message);
    };
    this.uploader.onSuccessItem = (item, response, status, headers) => {
      this.onSuccessUpload.emit({
        documentType: this.documentType,
        filename: item.file.name,
        title: this.title,
      });
      const notify = this.getUploadSucceedTranslation();
      this.showSuccess(notify.header, notify.message);
    };
  }

  ngOnChanges(_changes: SimpleChanges) {
    this.uploader.options.url = this.url;
    this.uploader.authToken = this.authToken;
  }

  fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
  }

  onFileDrop() {
    this.file.nativeElement.value = '';
  }

  private getUploadSucceedTranslation(): UploadMessageTranslation {
    const translation: UploadMessageTranslation = {
      header: '',
      message: '',
    };

    this.translate.get('fileUpload.message.header').pipe(
      flatMap(res => {
        translation.header = res;
        return this.translate.get('fileUpload.message.succeed');
      }),
    ).subscribe(res => {
      translation.message = res;
    });

    return translation;
  }

  private getUploadFailedTranslation(): UploadMessageTranslation {
    const translation: UploadMessageTranslation = {
      header: '',
      message: '',
    };

    this.translate.get('fileUpload.message.header').pipe(
      flatMap(res => {
        translation.header = res;
        return this.translate.get('fileUpload.message.failed');
      }),
    ).subscribe(res => {
      translation.message = res;
    });

    return translation;
  }

  private manageSingleQueue() {
    if (!this.uploader || !this.uploader.queue) { return; }
    while (this.uploader.queue.length > 1) {
      this.uploader.queue.shift();
    }
  }

  private showError(title: string, message: string) {
    this.toastrService.error(
      message,
      title,
      {
        closeButton: true,
        timeOut: 4000,
        onActivateTick: true,
      },
    );
  }

  private showSuccess(title: string, message: string) {
    this.toastrService.success(
      message,
      title,
      {
        closeButton: true,
        timeOut: 4000,
        onActivateTick: true,
      },
    );
  }
}
